import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { getFAQs, createFAQ, updateFAQ, deleteFAQ, getFaqQuestions, updateFaqQuestion, deleteFaqQuestion, answerFaqQuestion, getUiStrings, updateUiStrings, getFaqCategories } from "../../lib/api";
import AdminFaqCategories from "./AdminFaqCategories";
import { Plus, Save, Trash2, HelpCircle, MessageSquare, CheckCircle, XCircle, Mail, User, Pencil, X, Send, Loader2, Tag, Eye, EyeOff } from "lucide-react";
import { logger } from "../../lib/logger";
import { DEFAULT_EMPTY_FAQ, EMPTY_FAQ, EMPTY_FAQ_QUESTIONS, TOAST_SAVE_ERROR, TOAST_ADD_ERROR, TOAST_REMOVE_ERROR, TOAST_ADDED, TOAST_UPDATED, TOAST_REMOVED, TOAST_NO_PERMISSION, TOAST_REQUIRED, TIP_EDIT, TIP_DELETE, PLH_FAQ_QUESTION, PLH_FAQ_ANSWER, PLH_FAQ_REPLY, PLH_FAQ_EYEBROW, PLH_FAQ_HEADLINE, PLH_FAQ_DESC } from "../../lib/constants";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";

function Field({ label, value, onChange, multiline, placeholder }) {
  const Tag = multiline ? "textarea" : "input";
  return (
    <label className="block">
      <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">{label}</span>
      <Tag className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" rows={multiline ? 4 : undefined} value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </label>
  );
}

export default function AdminFaq({ readOnly = false, subTabModes = {}, defaultTab, onTabChange, saving: parentSaving, activeLang: propLang }) {
  const [faqs, setFaqs] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const TAB_MAP = { faqs: "faqs", questions: "questions", categories: "categories", texts: "texts" };
  const [tab, setTab] = useState(TAB_MAP[defaultTab] || defaultTab || "faqs");
  const prevDefaultTab = useRef(defaultTab);
  useEffect(() => {
    const mapped = TAB_MAP[defaultTab] || defaultTab;
    if (mapped && mapped !== prevDefaultTab.current) { setTab(mapped); prevDefaultTab.current = mapped; }
  }, [defaultTab]);
  const [faqCategoryFilter, setFaqCategoryFilter] = useState("");
  const filteredFaqs = faqCategoryFilter ? faqs.filter(f => f.category_uid === faqCategoryFilter) : faqs;
  const [qFilter, setQFilter] = useState("all");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [form, setForm] = useState(DEFAULT_EMPTY_FAQ);

  useEffect(() => {
    if (!showAddForm && !editingFaq) return;
    function onKey(e) {
      if (e.key === "Escape") { setShowAddForm(false); cancelEdit(); }
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        if (editingFaq) handleUpdate(editingFaq);
        else if (showAddForm) handleCreate();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showAddForm, editingFaq, form]);
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [answeringId, setAnsweringId] = useState(null);
  const [answeredId, setAnsweredId] = useState(null);
  const [faqTexts, setFaqTexts] = useState({ eyebrow: "", headline: "", description: "" });

  useEffect(() => {
    async function load() {
      setLoading(true);
      const promises = [];
      if (tab === "faqs" || tab === "categories") {
        promises.push(
          getFAQs(propLang).then(d => setFaqs(d || [])).catch(() => {}),
          getFaqCategories(propLang).then(d => setCategories(Array.isArray(d) ? d.filter(c => !c.deleted_at) : [])).catch(() => {}),
        );
      }
      if (tab === "questions") {
        promises.push(getFaqQuestions().then(d => setQuestions(d || [])).catch(() => {}));
      }
      if (tab === "texts") {
        promises.push(
          getUiStrings(propLang).catch(() => ({})).then(ui => {
            const section = ui?.faq_section || {};
            setFaqTexts({ eyebrow: section.eyebrow || "", headline: section.headline || "", description: section.description || "" });
          }).catch(() => {}),
        );
      }
      try { await Promise.allSettled(promises); } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [propLang, tab]);

  function isSubReadOnly(sub) { return readOnly || subTabModes[`faq_${sub}`] === "readonly"; }

  async function handleCreate() {
    if (isSubReadOnly("faqs")) { toast.warning(TOAST_NO_PERMISSION); return; }
    if (!form.question.trim() || !form.answer.trim()) return toast.error(TOAST_REQUIRED);
    try {
      const created = await createFAQ({ question: form.question.trim(), answer: form.answer.trim(), category_uid: form.categoryUid || null, sort_order: faqs.length }, propLang);
      if (created?.uid) created.category_uid = created.category_uid || form.categoryUid || null;
      setFaqs([...faqs, created]);
      setShowAddForm(false);
      setForm({ ...DEFAULT_EMPTY_FAQ });
      toast.success(TOAST_ADDED);
    } catch (err) { toast.error(err.message || TOAST_ADD_ERROR); }
  }

  async function handleUpdate(id) {
    if (isSubReadOnly("faqs")) { toast.warning(TOAST_NO_PERMISSION); return; }
    setSaving(true);
    try {
      const updatedRow = await updateFAQ(id, { question: form.question, answer: form.answer, category_uid: form.categoryUid || null }, propLang);
      setFaqs((prev) => prev.map((f) => (f.uid === id ? { ...f, question: form.question, answer: form.answer, category_uid: form.categoryUid || null } : f)));
      setEditingFaq(null);
      toast.success(TOAST_UPDATED);
    } catch (err) { toast.error(TOAST_SAVE_ERROR); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (isSubReadOnly("faqs")) { toast.warning(TOAST_NO_PERMISSION); return; }
    try {
      await deleteFAQ(id);
      setFaqs((prev) => prev.filter((f) => f.uid !== id));
      if (editingFaq === id) setEditingFaq(null);
      setFaqToDelete(null);
      toast.success(TOAST_REMOVED);
    } catch (err) { toast.error(TOAST_REMOVE_ERROR); }
  }

  function startEdit(faq) {
    setEditingFaq(faq.uid);
    setForm({ question: faq.question, answer: faq.answer, categoryUid: faq.category_uid || "" });
  }

  function cancelEdit() { setEditingFaq(null); setForm({ ...DEFAULT_EMPTY_FAQ }); }

  async function handleAnswerQuestion(q) {
    if (isSubReadOnly("questions")) { toast.warning(TOAST_NO_PERMISSION); return; }
    if (!q.answer?.trim()) return toast.error("Escreva uma resposta antes de enviar");
    setAnsweringId(q.uid);
    try {
      const updated = await answerFaqQuestion({ id: q.uid, answer: q.answer });
      setQuestions((prev) => prev.map((qi) => (qi.uid === q.uid ? { ...qi, ...updated } : qi)));
      setAnsweredId(q.uid);
      toast.success("Resposta enviada por email para " + q.name);
      setTimeout(() => setAnsweredId(null), 3000);
    } catch (err) { toast.error(TOAST_SAVE_ERROR); }
    finally { setAnsweringId(null); }
  }

  async function handleToggleQuestionStatus(id, current) {
    if (isSubReadOnly("questions")) { toast.warning(TOAST_NO_PERMISSION); return; }
    const next = current === "published" ? "pending" : "published";
    try {
      await updateFaqQuestion(id, { status: next });
      setQuestions((prev) => prev.map((q) => (q.uid === id ? { ...q, status: next } : q)));
      toast.success(next === "published" ? "Publicada no site" : "Arquivada");
    } catch (err) { toast.error(TOAST_SAVE_ERROR); }
  }

  async function handleDeleteQuestion(id) {
    if (isSubReadOnly("questions")) { toast.warning(TOAST_NO_PERMISSION); return; }
    try {
      await deleteFaqQuestion(id);
      setQuestions((prev) => prev.filter((q) => q.uid !== id));
      setQuestionToDelete(null);
      toast.success(TOAST_REMOVED);
    } catch (err) { toast.error(TOAST_REMOVE_ERROR); }
  }

  const pendingCount = questions.filter((q) => q.status === "pending").length;
  const answeredCount = questions.filter((q) => q.status === "answered").length;
  const publishedCount = questions.filter((q) => q.status === "published").length;
  const filteredQuestions = qFilter === "all" ? questions : questions.filter((q) => q.status === qFilter);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 border-2 border-champagne border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-id="admin-faq" data-testid="admin-faq-tab" className="space-y-6">

      {/* ========== FAQs ========== */}
      {tab === "faqs" && (
        <div>
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <button onClick={() => setFaqCategoryFilter("")}
              className={`px-4 py-2 text-[9px] font-bold tracking-wider uppercase rounded-full border transition-all ${faqCategoryFilter === "" ? "bg-softblack text-white border-softblack" : "bg-white text-warmgray border-fendi/30 hover:border-fendi/60"}`}>
              Todas <span className="opacity-60">({faqs.length})</span>
            </button>
            {categories.map(c => (
              <button key={c.uid} onClick={() => { setFaqCategoryFilter(c.uid); setShowAddForm(false); cancelEdit(); }}
                className={`px-4 py-2 text-[9px] font-bold tracking-wider uppercase rounded-full border transition-all ${faqCategoryFilter === c.uid ? "bg-softblack text-white border-softblack" : "bg-white text-warmgray border-fendi/30 hover:border-fendi/60"}`}>
                {c.name} <span className="opacity-60">({faqs.filter(f => f.category_uid === c.uid).length})</span>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-4 mb-6">
            <p className="text-xs text-warmgray">
              {faqCategoryFilter
                ? `Mostrando FAQs de "${(categories.find(c => c.uid === faqCategoryFilter)?.name) || ""}"`
                : `${faqs.length} FAQ${faqs.length !== 1 ? "s" : ""} cadastrada${faqs.length !== 1 ? "s" : ""}`}
            </p>
            {faqCategoryFilter && (
              <button onClick={() => { setShowAddForm(!showAddForm); cancelEdit(); if (!showAddForm) setForm({ ...form, categoryUid: faqCategoryFilter }); }}
                className="btn-primary !py-2 !px-4 !text-[10px] flex items-center gap-2 shrink-0">
                <Plus size={14} /> Nova FAQ
              </button>
            )}
          </div>

          {editingFaq && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg mx-4 w-full" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Pencil size={15} /></div>
                    <p className="text-sm font-medium text-softblack">Editar FAQ</p>
                  </div>
                  <button onClick={cancelEdit} className="p-1 text-warmgray hover:text-softblack"><X size={16} /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Pergunta <span className="text-red-400">*</span></span>
                    <input value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Resposta <span className="text-red-400">*</span></span>
                    <textarea value={form.answer} onChange={e => setForm({ ...form, answer: e.target.value })} rows={4} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all resize-none" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Categoria</span>
                    <select value={form.categoryUid} onChange={e => setForm({ ...form, categoryUid: e.target.value })} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all">
                      <option value="">— Sem categoria —</option>
                      {categories.map(c => <option key={c.uid} value={c.uid}>{c.name}</option>)}
                    </select>
                  </div>
                </div>
                {!isSubReadOnly("faqs") && (
                  <div className="flex justify-end gap-2 mt-6">
                    <button onClick={cancelEdit} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Cancelar</button>
                    <button onClick={() => handleUpdate(editingFaq)} disabled={saving} className="px-4 py-2 text-xs font-medium text-white bg-softblack rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"><Save size={12} /> Guardar</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {showAddForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg mx-4 w-full" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Plus size={15} /></div>
                    <p className="text-sm font-medium text-softblack">Nova FAQ {faqCategoryFilter ? `em "${(categories.find(c => c.uid === faqCategoryFilter)?.name) || ""}"` : ""}</p>
                  </div>
                  <button onClick={() => setShowAddForm(false)} className="p-1 text-warmgray hover:text-softblack"><X size={16} /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Pergunta <span className="text-red-400">*</span></span>
                    <input value={form.question} onChange={e => setForm({ ...form, question: e.target.value })} placeholder={PLH_FAQ_QUESTION} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-wider text-warmgray font-medium">Resposta <span className="text-red-400">*</span></span>
                    <textarea value={form.answer} onChange={e => setForm({ ...form, answer: e.target.value })} rows={4} placeholder={PLH_FAQ_ANSWER} className="w-full mt-1 px-3 py-2 text-xs border border-fendi/30 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all resize-none" />
                  </div>
                </div>
                {!isSubReadOnly("faqs") && (
                  <div className="flex justify-end gap-2 mt-6">
                    <button onClick={() => setShowAddForm(false)} className="px-4 py-2 text-xs font-medium text-warmgray border border-fendi/30 rounded-lg hover:bg-fendi/5">Cancelar</button>
                    <button onClick={handleCreate} className="px-4 py-2 text-xs font-medium text-white bg-softblack rounded-lg hover:opacity-90 flex items-center gap-1.5"><Save size={12} /> Adicionar</button>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-3">
            {filteredFaqs.length === 0 && !showAddForm && (
              <div className="text-center text-warmgray text-sm py-16 bg-fendi/5 border border-dashed border-fendi/40 rounded-xl">{EMPTY_FAQ}</div>
            )}
            {filteredFaqs.map((faq) => (
              <div key={faq.uid} className="bg-white border border-fendi/30 rounded-xl overflow-hidden hover:shadow-sm transition-shadow">
                <div className="p-5 flex items-start justify-between gap-4 group">
                  <div className="flex gap-3 min-w-0 flex-1">
                    <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center shrink-0 mt-0.5">
                      <HelpCircle size={15} className="text-champagne" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-softblack">{faq.question}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {faq.category_uid && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-fendi/10 text-[9px] tracking-wider rounded-full text-warmgray font-medium">
                            <Tag size={9} /> {categories.find(c => c.uid === faq.category_uid)?.name || ""}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-warmgray font-light mt-2 leading-relaxed line-clamp-2">{faq.answer}</p>
                    </div>
                  </div>
                  {!isSubReadOnly("faqs") && (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => startEdit(faq)} className="p-2 text-warmgray hover:text-champagne rounded-lg hover:bg-champagne/5 transition-colors" title={TIP_EDIT}><Pencil size={13} /></button>
                      <button onClick={() => setFaqToDelete(faq.uid)} className="p-2 text-warmgray hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors" title={TIP_DELETE}><Trash2 size={13} /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ========== CATEGORIAS ========== */}
      {tab === "categories" && <AdminFaqCategories activeLang={propLang || "pt-PT"} />}

      {/* ========== TEXTOS ========== */}
      {tab === "texts" && (
        <div className="bg-white border border-fendi/40 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-fendi/10 bg-fendi/5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center text-champagne"><Tag size={14} /></div>
              <span className="text-xs font-medium text-softblack uppercase tracking-wider">Textos da Secção FAQ</span>
            </div>
            {!readOnly && (
              <button onClick={() => { const d = faqTexts; updateUiStrings("faq_section", d, propLang).then(() => toast.success(TOAST_UPDATED)).catch(() => toast.error(TOAST_SAVE_ERROR)); }} disabled={parentSaving} className="btn-primary !py-1.5 !px-3 !text-[9px]">
                <Save size={11} className="mr-1.5" /> Guardar
              </button>
            )}
          </div>
          <div className="p-6 grid sm:grid-cols-3 gap-4">
            <div>
              <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Eyebrow</span>
              <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={faqTexts.eyebrow}
                onChange={e => setFaqTexts(p => ({ ...p, eyebrow: e.target.value }))} placeholder={PLH_FAQ_EYEBROW} />
            </div>
            <div>
              <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Headline</span>
              <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={faqTexts.headline}
                onChange={e => setFaqTexts(p => ({ ...p, headline: e.target.value }))} placeholder={PLH_FAQ_HEADLINE} />
            </div>
            <div>
              <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Descrição</span>
              <input className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" value={faqTexts.description}
                onChange={e => setFaqTexts(p => ({ ...p, description: e.target.value }))} placeholder={PLH_FAQ_DESC} />
            </div>
          </div>
        </div>
      )}

      {/* ========== RECEBIDAS ========== */}
      {tab === "questions" && (
        <div>
          <div className="flex items-center justify-between gap-4 mb-6">
            <p className="text-xs text-warmgray">{questions.length} pergunta{questions.length !== 1 ? "s" : ""} recebida{questions.length !== 1 ? "s" : ""}</p>
          </div>

          <div className="flex gap-2 mb-6 flex-wrap">
            {[
              { key: "all", label: "Todas", count: questions.length },
              { key: "pending", label: "Pendentes", count: pendingCount, color: "text-amber-600" },
              { key: "answered", label: "Respondidas", count: answeredCount, color: "text-blue-600" },
              { key: "published", label: "Publicadas", count: publishedCount, color: "text-green-600" },
            ].map((f) => (
              <button key={f.key} onClick={() => setQFilter(f.key)}
                className={`px-4 py-2 text-[9px] font-bold tracking-wider uppercase rounded-full border transition-all ${qFilter === f.key ? "bg-softblack text-white border-softblack" : "bg-white text-warmgray border-fendi/30 hover:border-fendi/60"}`}>
                {f.label} <span className={qFilter === f.key ? "text-white/60" : f.color || "opacity-60"}>({f.count})</span>
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {filteredQuestions.length === 0 && (
              <div className="text-center text-warmgray text-sm py-16 bg-fendi/5 border border-dashed border-fendi/40 rounded-xl">
                {qFilter === "all" ? EMPTY_FAQ_QUESTIONS : `Nenhuma pergunta ${qFilter}.`}
              </div>
            )}
            {filteredQuestions.map((q) => {
              const isSending = answeringId === q.uid;
              const isSent = answeredId === q.uid;
              const statusColors = { published: "border-green-300 bg-green-50 text-green-700", answered: "border-blue-300 bg-blue-50 text-blue-700", pending: "border-amber-300 bg-amber-50 text-amber-700" };
              const statusLabels = { published: "Publicada", answered: "Respondida", pending: "Pendente" };
              return (
                <div key={q.uid} className={`bg-white border rounded-xl overflow-hidden transition-all duration-300 ${isSent ? "border-champagne/60 shadow-md" : "border-fendi/30"}`}>
                  <div className="p-5 md:p-6">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-fendi/10 flex items-center justify-center shrink-0">
                          <User size={14} className="text-warmgray" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-softblack">{q.name}</p>
                          <a href={`mailto:${q.email}`} className="text-[10px] text-champagne hover:text-softblack inline-flex items-center gap-1 mt-0.5">
                            <Mail size={10} /> {q.email}
                          </a>
                        </div>
                      </div>
                      <span className={`text-[9px] font-medium px-2.5 py-1 rounded-full border ${statusColors[q.status] || "border-fendi/30 text-warmgray"}`}>
                        {statusLabels[q.status] || q.status}
                      </span>
                    </div>

                    <div className="border-l-2 border-champagne/30 pl-4 py-3 mb-4 bg-fendi/5 rounded-r-lg">
                      <p className="text-sm text-softblack leading-relaxed italic">&ldquo;{q.question}&rdquo;</p>
                    </div>

                    <div className="space-y-3">
                      <label className="block">
                        <span className="text-[9px] uppercase tracking-wider text-warmgray font-medium">Resposta</span>
                        <textarea className="w-full text-xs px-3 py-2 mt-1.5 border border-fendi/20 rounded-lg bg-transparent focus:border-champagne focus:ring-1 focus:ring-champagne/20 outline-none transition-all" rows={3}
                          value={q.answer || ""}
                          onChange={(e) => setQuestions((prev) => prev.map((qi) => qi.uid === q.uid ? { ...qi, answer: e.target.value } : qi))}
                          placeholder={PLH_FAQ_REPLY} disabled={isSending} />
                      </label>

                      {isSent && (
                        <div className="flex items-center gap-2 px-4 py-2.5 bg-champagne/10 border border-champagne/30 rounded-lg text-xs text-softblack">
                          <CheckCircle size={14} className="text-champagne shrink-0" /> Resposta enviada para {q.email}
                        </div>
                      )}

                      {!isSubReadOnly("questions") && (
                        <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
                          <div className="flex items-center gap-2">
                            <button onClick={() => handleToggleQuestionStatus(q.uid, q.status)}
                              className={`text-[10px] flex items-center gap-1.5 px-3 py-1.5 rounded-lg border transition-all ${q.status === 'published' ? 'border-green-300 text-green-700 bg-green-50' : 'border-fendi/30 text-warmgray hover:text-softblack hover:bg-fendi/5'}`}>
                              {q.status === 'published' ? <EyeOff size={11} /> : <Eye size={11} />}
                              {q.status === 'published' ? 'Arquivar' : 'Publicar'}
                            </button>
                            <button onClick={() => setQuestionToDelete(q.uid)} className="text-[10px] text-warmgray hover:text-red-600 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-fendi/30 hover:border-red-200 hover:bg-red-50 transition-all">
                              <Trash2 size={11} /> Remover
                            </button>
                          </div>
                          <button onClick={() => handleAnswerQuestion(q)} disabled={isSending || !q.answer?.trim()}
                            className={`btn-primary !py-2 !px-5 !text-[10px] flex items-center gap-2 ${isSent ? '!bg-champagne !text-softblack' : ''}`}>
                            {isSending ? <><Loader2 size={12} className="animate-spin" /> Enviando...</>
                            : isSent ? <><CheckCircle size={12} /> Enviado</>
                            : <><Send size={12} /> {q.answer?.trim() ? "Responder e Enviar" : "Responder"}</>}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <AlertDialog open={!!faqToDelete} onOpenChange={() => setFaqToDelete(null)}>
        <AlertDialogContent className="bg-white border border-fendi/40 rounded-xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-medium text-softblack">Eliminar FAQ?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-warmgray leading-relaxed">Esta ação removerá permanentemente esta pergunta frequente do site.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-3 mt-6">
            <AlertDialogCancel className="btn-ghost !border-fendi/30 !py-2 !px-6 text-xs">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(faqToDelete)} className="bg-red-600 text-white hover:bg-red-700 !py-2 !px-6 text-xs rounded-lg">Confirmar Eliminação</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!questionToDelete} onOpenChange={() => setQuestionToDelete(null)}>
        <AlertDialogContent className="bg-white border border-fendi/40 rounded-xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-medium text-softblack">Eliminar Pergunta?</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-warmgray leading-relaxed">Esta ação removerá permanentemente esta pergunta e sua resposta.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-3 mt-6">
            <AlertDialogCancel className="btn-ghost !border-fendi/30 !py-2 !px-6 text-xs">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDeleteQuestion(questionToDelete)} className="bg-red-600 text-white hover:bg-red-700 !py-2 !px-6 text-xs rounded-lg">Confirmar Eliminação</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
